import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellOff, CheckCheck } from 'lucide-react';
import { ROUTES } from '../../app/config';
import { formatRelative } from '../../lib/format';
import { cn } from '../../lib/cn';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../services/notifications.service';
import { Button } from '../ui';

export function NotificationsPanel({ onClose }: { onClose: () => void }): JSX.Element {
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent): void => {
      if (!panelRef.current?.contains(event.target as Node)) onClose();
    };
    // `setTimeout` evita que el click que abre el panel lo cierre al instante.
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  const items = data?.items ?? [];

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] animate-fade-in overflow-hidden rounded-card border border-border bg-surface shadow-overlay"
      role="dialog"
      aria-label="Notificaciones"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-text-primary">Notificaciones</h3>
        {items.some((item) => !item.readAt) ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAllRead.mutate()}
            loading={markAllRead.isPending}
            leftIcon={<CheckCheck className="h-3.5 w-3.5" aria-hidden />}
          >
            Marcar todas
          </Button>
        ) : null}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <p className="px-4 py-6 text-center text-sm text-text-muted">Cargando...</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-8 text-center">
            <BellOff className="mb-2 h-5 w-5 text-text-muted" aria-hidden />
            <p className="text-sm text-text-muted">No tienes notificaciones.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (!item.readAt) markRead.mutate(item.id);
                    /*
                     * A donde lleva cada aviso. El anuncio va al canal y no a
                     * una pantalla suya: el contenido entero esta alli, con su
                     * formato, y ademas se ve lo que se haya publicado antes.
                     */
                    if (item.entityType === 'ticket' && item.entityId) {
                      navigate(ROUTES.ticketDetail(item.entityId));
                    } else if (item.entityType === 'announcement') {
                      navigate(ROUTES.announcements);
                    }
                    onClose();
                  }}
                  className={cn(
                    'w-full px-4 py-3 text-left transition-colors hover:bg-surface-hover',
                    !item.readAt && 'bg-primary-soft/40',
                  )}
                >
                  <p className="text-sm font-medium text-text-primary">{item.title}</p>
                  {item.body ? (
                    <p className="mt-0.5 truncate text-[13px] text-text-secondary">{item.body}</p>
                  ) : null}
                  <p className="mt-1 text-[11px] text-text-muted">
                    {formatRelative(item.createdAt)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
